import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { submitBugReport } from '../../services/bugReportService';
import './BugReportSheet.css';

interface BugReportSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SUCCESS_AUTO_CLOSE_MS = 2500;

const initialFormData = {
  name: '',
  mail: '',
  telefono: '',
  description: '',
};

function BugReportSheet({ isOpen, onClose }: BugReportSheetProps) {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      // Reset for next time the sheet is opened, after the close animation.
      const timeout = setTimeout(() => {
        setFormData(initialFormData);
        setApiError('');
        setSuccess(false);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({ ...prev, telefono: value || '' }));
  };

  const isEmailValid = formData.mail.trim() === '' || EMAIL_REGEX.test(formData.mail);
  const isFormValid =
    formData.name.trim() !== '' &&
    formData.mail.trim() !== '' &&
    EMAIL_REGEX.test(formData.mail) &&
    formData.telefono.trim() !== '' &&
    formData.description.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || loading) return;

    setApiError('');
    setLoading(true);
    try {
      await submitBugReport(formData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, SUCCESS_AUTO_CLOSE_MS);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      setApiError(message || 'No pudimos enviar tu reporte. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = () => {
    onClose();
  };

  return (
    <div className="bug-report-backdrop" onClick={handleBackdropClick}>
      <div
        className="bug-report-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="bug-report-close"
          onClick={onClose}
          aria-label="Cerrar"
        >
          &times;
        </button>

        {success ? (
          <div className="bug-report-success">
            <h2 id="bug-report-title">¡Gracias!</h2>
            <p>Recibimos tu reporte y te contactaremos a la brevedad.</p>
          </div>
        ) : (
          <>
            <h2 id="bug-report-title" className="bug-report-title">
              ¿Tenés un problema con el login?
            </h2>
            <p className="bug-report-intro">
              ¿U otro error con el que podamos ayudarte? Por favor reportalo por
              esta vía y te ayudaremos a la brevedad.
            </p>

            <form onSubmit={handleSubmit} className="bug-report-form">
              <div className="bug-report-field">
                <label htmlFor="bug-report-name">Nombre</label>
                <input
                  id="bug-report-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={100}
                  required
                  disabled={loading}
                  placeholder="Tu nombre"
                  className="bug-report-input"
                />
              </div>

              <div className="bug-report-field">
                <label htmlFor="bug-report-mail">Email</label>
                <input
                  id="bug-report-mail"
                  name="mail"
                  type="email"
                  value={formData.mail}
                  onChange={handleChange}
                  maxLength={100}
                  required
                  disabled={loading}
                  placeholder="tu@email.com"
                  className={`bug-report-input ${!isEmailValid ? 'bug-report-input--error' : ''}`}
                />
                {!isEmailValid && (
                  <span className="bug-report-field-error">Ingresá un email válido</span>
                )}
              </div>

              <div className="bug-report-field">
                <label htmlFor="bug-report-telefono">Teléfono</label>
                <PhoneInput
                  id="bug-report-telefono"
                  international
                  defaultCountry="AR"
                  value={formData.telefono}
                  onChange={handlePhoneChange}
                  maxLength={20}
                  disabled={loading}
                  placeholder="Ingresá tu número de teléfono"
                  className="bug-report-phone-input"
                />
              </div>

              <div className="bug-report-field">
                <label htmlFor="bug-report-description">Descripción del error</label>
                <textarea
                  id="bug-report-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  maxLength={1000}
                  required
                  disabled={loading}
                  placeholder="Contanos qué pasó..."
                  rows={4}
                  className="bug-report-input bug-report-textarea"
                />
              </div>

              {apiError && <div className="bug-report-error-banner">{apiError}</div>}

              <button
                type="submit"
                disabled={!isFormValid || loading}
                className="bug-report-submit"
              >
                {loading ? 'Enviando...' : 'Enviar reporte'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BugReportSheet;
